import React from 'react';
import { Box, Typography, Paper, Grid, Link, alpha } from '@mui/material';
import {
  AttachMoney, Flag, FolderOpen, WorkspacePremium, Insights, Phone, Email,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BRAND, SUPPORT_PHONE, SUPPORT_EMAIL } from '../login/brand';
import { DASH, flowColor } from './dashboardColors';

const formatCurrency = (amount, lang) => new Intl.NumberFormat(lang || 'en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(amount);

const FundingSnapshotCard = ({
  type = 'fastFunding',
  amountRequested = 0,
  currentStep = null, // { number, total, title } — null once every step is complete
  documentsUploaded = 0,
  programsUnlocked = 0,
  totalPrograms = 0,
  wide = false,
}) => {
  const { t, i18n } = useTranslation();
  const accent = flowColor(type);

  const tiles = [
    {
      icon: AttachMoney,
      label: t('dashboard.snapshot.amountRequested'),
      value: amountRequested > 0 ? formatCurrency(amountRequested, i18n.language) : t('dashboard.snapshot.notSet'),
      muted: !(amountRequested > 0),
    },
    {
      icon: Flag,
      label: t('dashboard.snapshot.currentStep'),
      value: currentStep
        ? t('dashboard.snapshot.stepOf', { number: currentStep.number, total: currentStep.total })
        : t('dashboard.snapshot.complete'),
      detail: currentStep ? currentStep.title : t('dashboard.snapshot.allDocsIn'),
    },
    {
      icon: FolderOpen,
      label: t('dashboard.snapshot.documentsUploaded'),
      value: String(documentsUploaded),
      muted: documentsUploaded === 0,
    },
    {
      icon: WorkspacePremium,
      label: t('dashboard.snapshot.programsUnlocked'),
      value: `${programsUnlocked} / ${totalPrograms}`,
      muted: programsUnlocked === 0,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 3, pb: 2, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <Insights sx={{ color: accent, fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: DASH.ink }}>
            {t('dashboard.snapshot.title')}
          </Typography>
        </Box>

        <Grid container spacing={1.5}>
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <Grid item xs={6} md={wide ? 3 : 6} key={tile.label}>
                <Box
                  sx={{
                    p: 1.5,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: tile.muted ? 'grey.50' : DASH.cream,
                    border: '1px solid',
                    borderColor: tile.muted ? 'grey.100' : alpha(DASH.sand, 0.55),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Icon sx={{ fontSize: 16, color: tile.muted ? 'grey.500' : accent }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.2 }}>
                      {tile.label}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: '1.05rem', color: tile.muted ? 'text.secondary' : DASH.brown }}
                  >
                    {tile.value}
                  </Typography>
                  {tile.detail && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                      {tile.detail}
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Advisor contact */}
      <Box sx={{ px: 3, py: 2, background: BRAND.heroGradient, color: '#fff' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
          {t('dashboard.snapshot.questions')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.5 }}>
          <Link
            href={`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, '')}`}
            underline="hover"
            sx={{ color: BRAND.sand, display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, fontSize: '0.85rem' }}
          >
            <Phone sx={{ fontSize: 16 }} />
            {SUPPORT_PHONE}
          </Link>
          <Link
            href={`mailto:${SUPPORT_EMAIL}`}
            underline="hover"
            sx={{ color: BRAND.sand, display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, fontSize: '0.85rem', wordBreak: 'break-all' }}
          >
            <Email sx={{ fontSize: 16 }} />
            {SUPPORT_EMAIL}
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default FundingSnapshotCard;
